import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';

import type { EventBus } from '@/core/EventBus';
import type { UnitFactory } from '@/units/UnitFactory';
import type { UnitRenderer } from '@/units/UnitRenderer';
import type { BenchManager } from '@/placement/BenchManager';
import { Position } from '@/ecs/Component';
import { UnitData, Stats, Owner, UnitMesh } from '@/ecs/components/UnitComponents';
import { getUnitDefinition, STAR_MULTIPLIERS } from '@/units/UnitDefinitions';

/**
 * Merge events
 */
export const MergeEvents = {
  MERGE_AVAILABLE: 'merge:available',
  MERGE_COMPLETED: 'merge:completed',
  MERGE_FAILED: 'merge:failed',
  MAX_STAR_REACHED: 'merge:maxStar',
} as const;

export interface MergeCompletedEvent {
  resultEntityId: string;
  consumedEntityIds: string[];
  unitType: string;
  newStarLevel: number;
}

/**
 * Maximum star level a unit can reach
 */
export const MAX_STAR_LEVEL = 4;

/**
 * Manages unit merging to increase star levels
 */
export class MergeSystem {
  private world: World;
  private eventBus: EventBus;
  private unitFactory: UnitFactory;
  private unitRenderer: UnitRenderer;
  private benchManager: BenchManager;

  constructor(
    world: World,
    eventBus: EventBus,
    unitFactory: UnitFactory,
    unitRenderer: UnitRenderer,
    benchManager: BenchManager
  ) {
    this.world = world;
    this.eventBus = eventBus;
    this.unitFactory = unitFactory;
    this.unitRenderer = unitRenderer;
    this.benchManager = benchManager;
  }

  /**
   * Check if two units can be merged
   */
  canMerge(entity1: Entity, entity2: Entity): boolean {
    const data1 = entity1.getComponent(UnitData);
    const data2 = entity2.getComponent(UnitData);
    const owner1 = entity1.getComponent(Owner);
    const owner2 = entity2.getComponent(Owner);

    if (!data1 || !data2 || !owner1 || !owner2) return false;

    // Must be same owner
    if (owner1.playerId !== owner2.playerId) return false;

    // Must be same unit type
    if (data1.unitType !== data2.unitType) return false;

    // Must be same star level
    if (data1.starLevel !== data2.starLevel) return false;

    // Neither can be at max star
    if (data1.starLevel >= MAX_STAR_LEVEL) return false;

    return true;
  }

  /**
   * Attempt to merge two units
   * The source unit is consumed, and the target unit is upgraded
   */
  merge(sourceEntity: Entity, targetEntity: Entity): Entity | null {
    if (!this.canMerge(sourceEntity, targetEntity)) {
      this.eventBus.emit(MergeEvents.MERGE_FAILED, {
        reason: 'Units cannot be merged',
        sourceId: sourceEntity.id,
        targetId: targetEntity.id,
      });
      return null;
    }

    const targetData = targetEntity.getComponent(UnitData)!;
    const targetStats = targetEntity.getComponent(Stats)!;
    const targetOwner = targetEntity.getComponent(Owner)!;
    const targetPosition = targetEntity.getComponent(Position);
    const targetMesh = targetEntity.getComponent(UnitMesh);

    const sourceOwner = sourceEntity.getComponent(Owner)!;

    const newStarLevel = targetData.starLevel + 1;
    const definition = getUnitDefinition(targetData.unitType);
    if (!definition) return null;

    // Calculate new stats
    const statMultiplier = STAR_MULTIPLIERS[newStarLevel] ?? 1;
    const baseStats = definition.baseStats;

    // Update target unit data
    (targetData as { starLevel: number }).starLevel = newStarLevel;

    // Update stats
    (targetStats as { attack: number }).attack = Math.floor(baseStats.attack * statMultiplier);
    (targetStats as { defense: number }).defense = Math.floor(baseStats.defense * statMultiplier);
    (targetStats as { abilityPower: number }).abilityPower = Math.floor(baseStats.abilityPower * statMultiplier);
    (targetStats as { maxHealth: number }).maxHealth = Math.floor(baseStats.maxHealth * statMultiplier);
    (targetStats as { currentHealth: number }).currentHealth = Math.floor(baseStats.maxHealth * statMultiplier);

    // Remove source unit
    if (sourceOwner.isOnBench) {
      this.benchManager.removeFromBench(sourceEntity.id, sourceOwner.playerId);
    }
    this.unitFactory.removeUnit(sourceEntity);

    // Update target mesh to show new star level
    if (targetMesh && targetPosition) {
      // Remove old mesh
      this.unitRenderer.removeUnitMesh(targetMesh.meshId);

      // Create new mesh with updated star level
      const newMeshId = this.unitRenderer.createUnitMesh(
        targetEntity.id,
        definition.meshType,
        definition.color,
        targetOwner.playerId,
        newStarLevel,
        { q: targetPosition.q, r: targetPosition.r },
        definition.model,
        definition.scale
      );

      // Update mesh component
      (targetMesh as { meshId: string }).meshId = newMeshId;
    }

    this.eventBus.emit(MergeEvents.MERGE_COMPLETED, {
      resultEntityId: targetEntity.id,
      consumedEntityIds: [sourceEntity.id],
      unitType: targetData.unitType,
      newStarLevel,
    } as MergeCompletedEvent);

    console.log(`Merged ${targetData.name} to ${newStarLevel}★`);

    return targetEntity;
  }

  /**
   * Upgrade a unit by one star level (for shop merge-on-place)
   * Unlike merge(), this doesn't require a source unit to consume
   */
  upgradeUnit(entity: Entity): Entity | null {
    const unitData = entity.getComponent(UnitData);
    const stats = entity.getComponent(Stats);
    const owner = entity.getComponent(Owner);
    const position = entity.getComponent(Position);
    const mesh = entity.getComponent(UnitMesh);

    if (!unitData || !stats || !owner) return null;
    if (unitData.starLevel >= MAX_STAR_LEVEL) return null;

    const newStarLevel = unitData.starLevel + 1;
    const definition = getUnitDefinition(unitData.unitType);
    if (!definition) return null;

    // Calculate new stats
    const statMultiplier = STAR_MULTIPLIERS[newStarLevel] ?? 1;
    const baseStats = definition.baseStats;

    // Update unit data
    (unitData as { starLevel: number }).starLevel = newStarLevel;

    // Update stats
    (stats as { attack: number }).attack = Math.floor(baseStats.attack * statMultiplier);
    (stats as { defense: number }).defense = Math.floor(baseStats.defense * statMultiplier);
    (stats as { abilityPower: number }).abilityPower = Math.floor(baseStats.abilityPower * statMultiplier);
    (stats as { maxHealth: number }).maxHealth = Math.floor(baseStats.maxHealth * statMultiplier);
    (stats as { currentHealth: number }).currentHealth = Math.floor(baseStats.maxHealth * statMultiplier);

    // Update mesh to show new star level
    if (mesh && position) {
      // Remove old mesh
      this.unitRenderer.removeUnitMesh(mesh.meshId);

      // Create new mesh with updated star level
      const newMeshId = this.unitRenderer.createUnitMesh(
        entity.id,
        definition.meshType,
        definition.color,
        owner.playerId,
        newStarLevel,
        { q: position.q, r: position.r },
        definition.model,
        definition.scale
      );

      // Update mesh component
      (mesh as { meshId: string }).meshId = newMeshId;
    }

    this.eventBus.emit(MergeEvents.MERGE_COMPLETED, {
      resultEntityId: entity.id,
      consumedEntityIds: [],
      unitType: unitData.unitType,
      newStarLevel,
    } as MergeCompletedEvent);

    console.log(`Upgraded ${unitData.name} to ${newStarLevel}★`);

    return entity;
  }

  /**
   * Find all potential merge pairs for a player
   */
  findMergePairs(playerId: number): Array<[Entity, Entity]> {
    const pairs: Array<[Entity, Entity]> = [];

    // Get all units for this player
    const units = this.world.query(UnitData, Owner).filter((entity) => {
      const owner = entity.getComponent(Owner);
      return owner && owner.playerId === playerId;
    });

    // Group by type and star level
    const grouped = new Map<string, Entity[]>();
    for (const unit of units) {
      const data = unit.getComponent(UnitData)!;
      if (data.starLevel >= MAX_STAR_LEVEL) continue;

      const key = `${data.unitType}_${data.starLevel}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(unit);
    }

    // Find pairs
    for (const [_, entities] of grouped) {
      if (entities.length >= 2) {
        // Create pairs (can merge first two)
        pairs.push([entities[0], entities[1]]);
      }
    }

    return pairs;
  }

  /**
   * Check if a unit has a merge available
   */
  hasMergeAvailable(entity: Entity): Entity | null {
    const data = entity.getComponent(UnitData);
    const owner = entity.getComponent(Owner);

    if (!data || !owner) return null;
    if (data.starLevel >= MAX_STAR_LEVEL) return null;

    // Find another unit of same type and star
    const units = this.world.query(UnitData, Owner).filter((other) => {
      if (other.id === entity.id) return false;

      const otherData = other.getComponent(UnitData);
      const otherOwner = other.getComponent(Owner);

      return (
        otherData &&
        otherOwner &&
        otherOwner.playerId === owner.playerId &&
        otherData.unitType === data.unitType &&
        otherData.starLevel === data.starLevel
      );
    });

    return units.length > 0 ? units[0] : null;
  }

  /**
   * Auto-merge: automatically merge all possible pairs
   */
  autoMerge(playerId: number): number {
    let mergeCount = 0;

    while (true) {
      const pairs = this.findMergePairs(playerId);
      if (pairs.length === 0) break;

      const [source, target] = pairs[0];
      const result = this.merge(source, target);
      if (result) {
        mergeCount++;
      } else {
        break;
      }
    }

    return mergeCount;
  }

  /**
   * Emit merge available events for UI
   */
  checkAndNotifyMerges(playerId: number): void {
    const pairs = this.findMergePairs(playerId);

    for (const [entity1, entity2] of pairs) {
      const data = entity1.getComponent(UnitData)!;
      this.eventBus.emit(MergeEvents.MERGE_AVAILABLE, {
        entity1Id: entity1.id,
        entity2Id: entity2.id,
        unitType: data.unitType,
        currentStar: data.starLevel,
      });
    }
  }
}
